import { getPortalType, PORTAL } from '@/utilities/stytch/config'
import userService from '@/services/patient/user'
import type { FeatureFlagSchemaType as FeatureFlag } from '@/types/portal-api.generated'
import { logger } from '@/utilities/logging/logger'
import { errorAnalyticsPayload } from '@/utilities/analytics/errorAnalytics'
import { portalApiClient } from '@/platform/http'
import { isOnboardingCookieSetClient } from '@/utilities/onboardingCookie'

export type { FeatureFlag }

type FeatureFlagsResponse = { feature_flags: FeatureFlag[] }
type PatientFlagResult = { flags: FeatureFlag[]; provisional: boolean }

export type AllFlagsResult = {
  flags: Record<string, unknown>
  provisional: boolean
  error?: string
}

export const getFeatureFlagsForPreSignup = async (): Promise<FeatureFlag[]> => {
  const { data } = await portalApiClient.axios.get<FeatureFlagsResponse>('/v2/feature-flags')
  return data.feature_flags
}

const getFeatureFlagsForUser = async (): Promise<FeatureFlag[]> => {
  const user = await userService.getProfile()
  return user.feature_flags || []
}

function getFeatureFlagErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message
  if (typeof error === 'string' && error.trim()) return error
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string' &&
    error.message.trim()
  ) {
    return error.message
  }
  return 'Failed to fetch feature flags'
}

/**
 * Get feature flags for the patient portal. Provisional onboarding flags leave
 * the provider open to fetch the registered user's final flags after navigation.
 */
export const getPatientPortalFeatureFlags = async (
  isOnboarding: boolean,
): Promise<PatientFlagResult> => {
  if (isOnboarding) {
    const flags = await getFeatureFlagsForPreSignup()
    return { flags, provisional: true }
  }
  const flags = await getFeatureFlagsForUser()
  return { flags, provisional: false }
}

/**
 * Root feature-flag loading is Patient Portal only. Client Portal flags are
 * supplied by ClientPortalContextProvider from its shared `/partners` query.
 */
export const getAllFeatureFlags = async (): Promise<AllFlagsResult> => {
  const toMap = (flags: FeatureFlag[]): Record<string, unknown> =>
    flags.reduce(
      (acc, flag) => {
        acc[flag.name] = flag.value
        return acc
      },
      Object.create(null) as Record<string, unknown>,
    )

  const isOnboarding = isOnboardingCookieSetClient()

  try {
    if (getPortalType() === PORTAL.CLIENT) {
      return { flags: {}, provisional: false }
    }

    const result = await getPatientPortalFeatureFlags(isOnboarding)
    return { flags: toMap(result.flags), provisional: result.provisional }
  } catch (error) {
    logger.error('Error fetching feature flags', errorAnalyticsPayload(error))
    return {
      flags: {},
      provisional: isOnboarding,
      error: getFeatureFlagErrorMessage(error),
    }
  }
}
